// The in-call Breeze panel: shared notes, questions, and next steps, plus the
// private data one person keeps for themselves. Pure logic only. The Room
// Durable Object persists and broadcasts; this file decides what is allowed
// and what the result is, so the rules are testable without a socket.

export const PANEL_LISTS = ["notes", "questions", "steps"] as const;
export type PanelList = (typeof PANEL_LISTS)[number];

export const PANEL_LIMITS = {
  /** Characters in one note, question, or step. */
  itemChars: 500,
  /** Items in one shared list. */
  itemsPerList: 100,
  /** Characters in a next step's owner label. */
  ownerChars: 60,
  /** Characters in a person's private goal. */
  goalChars: 1000,
  /** Characters in a person's private notes. */
  privateNotesChars: 20000,
  /** Private questions one person keeps. */
  privateQuestions: 50,
  /** Shared-list changes one participant may make per window. */
  opsPerWindow: 20,
  opsWindowMs: 10_000,
  /** Private saves one socket may make per window (autosave is chatty). */
  privateSetsPerWindow: 30,
  /** Private rows one room keeps in total, so a hostile client cannot grow
   * the room's storage without bound. */
  privateRows: 300,
} as const;

export interface PanelItem {
  readonly id: string;
  readonly text: string;
  /** The participant id of the author, which is what edit rights hang on. */
  readonly authorId: string;
  readonly authorName: string;
  readonly createdAt: number;
  /** A note that is the author's own goal for the call, shared on purpose. */
  readonly goal?: boolean;
  /** Questions: the host marked it asked. */
  readonly asked?: boolean;
  /** Next steps: who it is for, free text. */
  readonly owner?: string;
  /** Next steps: finished. */
  readonly done?: boolean;
}

export interface PanelState {
  readonly notes: readonly PanelItem[];
  readonly questions: readonly PanelItem[];
  readonly steps: readonly PanelItem[];
}

export function emptyPanel(): PanelState {
  return { notes: [], questions: [], steps: [] };
}

export interface PanelActor {
  readonly id: string;
  readonly name: string;
  readonly isHost: boolean;
}

export type PanelOp =
  | { readonly op: "add"; readonly list: PanelList; readonly text: unknown; readonly owner?: unknown; readonly goal?: unknown }
  | {
      readonly op: "edit";
      readonly list: PanelList;
      readonly id: unknown;
      readonly text?: unknown;
      readonly owner?: unknown;
      readonly done?: unknown;
      readonly asked?: unknown;
    }
  | { readonly op: "delete"; readonly list: PanelList; readonly id: unknown };

export type PanelRefusal = "badList" | "badText" | "full" | "notFound" | "forbidden" | "badField";

export type PanelResult =
  | { readonly ok: true; readonly state: PanelState; readonly item: PanelItem; readonly removed?: undefined }
  | { readonly ok: true; readonly state: PanelState; readonly removed: string; readonly item?: undefined }
  | { readonly ok: false; readonly code: PanelRefusal };

export function isPanelList(value: unknown): value is PanelList {
  return typeof value === "string" && (PANEL_LISTS as readonly string[]).includes(value);
}

const CONTROL = new RegExp("[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]", "g");

/** Drop control characters other than newline and tab. */
export function stripControl(text: string): string {
  return text.replace(CONTROL, "");
}

/** Trim, collapse runs of blank lines, and refuse empty or oversized text. */
export function cleanText(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null;
  // Control characters other than newline and tab never belong in an item.
  const text = value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "").replace(/\n{3,}/g, "\n\n").trim();
  if (!text || [...text].length > max) return null;
  return text;
}

/** Same as `cleanText`, but empty is allowed (clearing a field). */
export function cleanOptional(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null;
  if (value.trim() === "") return "";
  return cleanText(value, max);
}

/** Apply one shared-list change on behalf of `actor`. The caller supplies the
 * clock and an id generator so the result is deterministic under test. */
export function applyPanelOp(
  state: PanelState,
  op: PanelOp,
  actor: PanelActor,
  now: number,
  newId: () => string,
): PanelResult {
  if (!isPanelList(op.list)) return { ok: false, code: "badList" };
  const list = state[op.list];

  if (op.op === "add") {
    const text = cleanText(op.text, PANEL_LIMITS.itemChars);
    if (text === null) return { ok: false, code: "badText" };
    if (list.length >= PANEL_LIMITS.itemsPerList) return { ok: false, code: "full" };
    let owner: string | undefined;
    if (op.list === "steps" && op.owner !== undefined) {
      const o = cleanOptional(op.owner, PANEL_LIMITS.ownerChars);
      if (o === null) return { ok: false, code: "badField" };
      if (o) owner = o;
    }
    const item: PanelItem = {
      id: newId(),
      text,
      authorId: actor.id,
      authorName: actor.name,
      createdAt: now,
      ...(op.list === "notes" && op.goal === true ? { goal: true } : {}),
      ...(op.list === "questions" ? { asked: false } : {}),
      ...(op.list === "steps" ? { done: false, ...(owner ? { owner } : {}) } : {}),
    };
    return { ok: true, state: { ...state, [op.list]: [...list, item] }, item };
  }

  if (typeof op.id !== "string") return { ok: false, code: "notFound" };
  const index = list.findIndex((i) => i.id === op.id);
  if (index < 0) return { ok: false, code: "notFound" };
  const current = list[index]!;
  const mayEdit = actor.isHost || current.authorId === actor.id;
  if (!mayEdit) return { ok: false, code: "forbidden" };

  if (op.op === "delete") {
    return { ok: true, state: { ...state, [op.list]: list.filter((_, i) => i !== index) }, removed: current.id };
  }

  let next: PanelItem = current;
  if (op.text !== undefined) {
    const text = cleanText(op.text, PANEL_LIMITS.itemChars);
    if (text === null) return { ok: false, code: "badText" };
    next = { ...next, text };
  }
  if (op.owner !== undefined) {
    if (op.list !== "steps") return { ok: false, code: "badField" };
    const o = cleanOptional(op.owner, PANEL_LIMITS.ownerChars);
    if (o === null) return { ok: false, code: "badField" };
    const { owner: _drop, ...rest } = next;
    next = o ? { ...rest, owner: o } : rest;
  }
  if (op.done !== undefined) {
    if (op.list !== "steps" || typeof op.done !== "boolean") return { ok: false, code: "badField" };
    next = { ...next, done: op.done };
  }
  if (op.asked !== undefined) {
    // Marking a question asked is the host's call, never the asker's.
    if (op.list !== "questions" || typeof op.asked !== "boolean") return { ok: false, code: "badField" };
    if (!actor.isHost) return { ok: false, code: "forbidden" };
    next = { ...next, asked: op.asked };
  }
  const nextList = list.map((i, n) => (n === index ? next : i));
  return { ok: true, state: { ...state, [op.list]: nextList }, item: next };
}

// ---- Private data ----------------------------------------------------------

export interface PrivateQuestion {
  readonly id: string;
  readonly text: string;
  readonly done: boolean;
  /** Breeze added it from a suggestion; the person can still edit or delete it. */
  readonly by?: "breeze";
}

/** What one person keeps for themselves. It lives in the Room under a key only
 * that person can present, and it is never in any broadcast or snapshot. */
export interface PrivateData {
  readonly goal: string;
  readonly notes: string;
  readonly questions: readonly PrivateQuestion[];
}

export function emptyPrivate(): PrivateData {
  return { goal: "", notes: "", questions: [] };
}

/** Validate a client-supplied private blob into a clean one, or null when it is
 * malformed or over a limit. Strict on purpose: a refused save is retried by
 * the client from its local copy, a silently truncated one would lose text. */
export function sanitizePrivate(value: unknown): PrivateData | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const goal = cleanOptional(v.goal ?? "", PANEL_LIMITS.goalChars);
  if (goal === null) return null;
  // Notes are free text typed live: the writer's own whitespace is kept, and
  // only control characters and an oversized body are refused.
  const rawNotes = v.notes ?? "";
  if (typeof rawNotes !== "string") return null;
  const notes = stripControl(rawNotes);
  if ([...notes].length > PANEL_LIMITS.privateNotesChars) return null;
  const rawQuestions = v.questions ?? [];
  if (!Array.isArray(rawQuestions) || rawQuestions.length > PANEL_LIMITS.privateQuestions) return null;
  const questions: PrivateQuestion[] = [];
  for (const q of rawQuestions) {
    if (typeof q !== "object" || q === null) return null;
    const r = q as Record<string, unknown>;
    const text = cleanText(r.text, PANEL_LIMITS.itemChars);
    if (text === null || typeof r.id !== "string" || r.id.length === 0 || r.id.length > 40) return null;
    questions.push({ id: r.id, text, done: r.done === true, ...(r.by === "breeze" ? { by: "breeze" as const } : {}) });
  }
  return { goal, notes, questions };
}

export function isEmptyPrivate(data: PrivateData): boolean {
  return data.goal === "" && data.notes === "" && data.questions.length === 0;
}

// ---- Phase B seam ----------------------------------------------------------

/** One line the assistant offers. Suggestions are never stored by the Room
 * and never part of the call record. */
export interface SuggestionItem {
  readonly id: string;
  readonly kind: "question" | "note" | "agenda" | "step";
  readonly text: string;
}

/** Who a `suggestions` message is for. `host` goes to the host's sockets
 * only, `self` to the one participant it was written for and nobody else.
 * `all` is part of the seam and the Room does not use it. */
export type SuggestionAudience = "host" | "all" | "self";

// ---- Rate limiting ---------------------------------------------------------

/** Sliding-window counter. In memory per Room instance: a hibernating Room
 * forgets it, which only ever loosens the limit for a socket that was quiet. */
export class RateWindow {
  private readonly stamps = new Map<string, number[]>();
  private readonly limit: number;
  private readonly windowMs: number;
  constructor(limit: number, windowMs: number) {
    this.limit = limit;
    this.windowMs = windowMs;
  }

  /** True when `key` may act now, and counts the action. */
  take(key: string, now: number): boolean {
    const recent = (this.stamps.get(key) ?? []).filter((t) => now - t < this.windowMs);
    if (recent.length >= this.limit) {
      this.stamps.set(key, recent);
      return false;
    }
    recent.push(now);
    this.stamps.set(key, recent);
    return true;
  }

  forget(key: string): void {
    this.stamps.delete(key);
  }
}

/** A client's private-store token: random, long enough to be unguessable. */
export function isPrivateToken(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{22,64}$/.test(value);
}
